import { StyleSheet, Platform } from 'react-native';
import { AdminColors } from '../../Admin/constants/adminColors';
import FontFamily from '../../../constants/fonts';

// Values follow the People Configuration v10 mockup tokens (--nav-*):
// text #2D2D2D / #616161 / #666, input border #949494, rows #EBEBEB, card radius 8, 24px card padding.
const TEXT_PRIMARY = AdminColors.textPrimary;
const TEXT_SECONDARY = AdminColors.textSecondary;
const TEXT_MUTED = AdminColors.gray400;
const INPUT_BORDER = '#949494';

export const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
  },
  scrollContent: {
    paddingHorizontal: 32,
    paddingTop: 24,
    paddingBottom: 64,
    gap: 20,
  },

  // ── Breadcrumb & Top Bar ──────────────────────────────────────────────────
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  breadcrumbText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textLink,
  },
  breadcrumbSeparator: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: TEXT_MUTED,
  },
  breadcrumbCurrent: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: TEXT_MUTED,
  },

  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    minHeight: 56,
  },
  headerTextGroup: {
    flex: 1,
    minWidth: 280,
  },
  pageTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 28,
    lineHeight: 40,
    color: TEXT_PRIMARY,
  },
  pageSubtitle: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: TEXT_SECONDARY,
    marginTop: 2,
  },
  topActionsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  btnSecondary: {
    borderWidth: 1,
    borderColor: AdminColors.blue800,
    backgroundColor: AdminColors.bgSurface,
    height: 40,
    paddingHorizontal: 24,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnSecondaryText: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    color: AdminColors.blue800,
  },
  btnPrimary: {
    backgroundColor: AdminColors.blue800,
    height: 40,
    paddingHorizontal: 24,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnPrimaryDisabled: {
    opacity: 0.4,
  },
  btnPrimaryText: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    color: AdminColors.textOnDark,
  },

  // ── Info Notification Banner (.tip) ───────────────────────────────────────
  infoBanner: {
    backgroundColor: AdminColors.blue25,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  infoBannerIcon: {
    marginTop: 1,
  },
  infoBannerText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: TEXT_SECONDARY,
    lineHeight: 20,
  },

  // ── Common Card Wrapper (.card) ───────────────────────────────────────────
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0px 0px 24px rgba(0, 94, 192, 0.08)',
      },
      default: {
        shadowColor: '#005EC0',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.08,
        shadowRadius: 24,
        elevation: 2,
      },
    }),
  },
  cardPad: {
    padding: 24,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  cardTitleGroup: {
    flex: 1,
  },
  cardTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 20,
    lineHeight: 28,
    color: TEXT_PRIMARY,
  },
  cardSubtitle: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: TEXT_SECONDARY,
    marginTop: 2,
  },

  // ── Tags (.tag: 24px high, 4px radius) ────────────────────────────────────
  badgeLocationGoverned: {
    backgroundColor: AdminColors.blue75,
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 4,
    justifyContent: 'center',
  },
  badgeLocationGovernedText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.blue900,
  },
  badgeInOrder: {
    backgroundColor: AdminColors.green75,
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 4,
    justifyContent: 'center',
  },
  badgeInOrderText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: '#0C420B',
  },
  badgeNeedsAttention: {
    backgroundColor: AdminColors.red75,
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 4,
    justifyContent: 'center',
  },
  badgeNeedsAttentionText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.red700,
  },
  badgeWeeksCount: {
    backgroundColor: AdminColors.blue75,
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 4,
    justifyContent: 'center',
  },
  badgeWeeksCountText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.blue900,
  },

  // ── Form Controls & Inputs (.field, .input) ───────────────────────────────
  formField: {
    flex: 1,
    minWidth: 200,
  },
  formLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: TEXT_PRIMARY,
    marginBottom: 8,
  },
  formHint: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: TEXT_MUTED,
    marginTop: 8,
  },

  inputWithSuffixRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  inputBox: {
    flex: 1,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: INPUT_BORDER,
    borderRadius: 6,
    height: 32,
    paddingHorizontal: 12,
    paddingVertical: 4,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: TEXT_PRIMARY,
  },
  inputBoxInvalid: {
    borderColor: AdminColors.red500,
  },
  suffixText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: TEXT_SECONDARY,
  },

  // ── Card 1: Location and Timeline (.people-config-settings) ───────────────
  locationSettingsGrid: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 24,
    flexWrap: 'wrap',
  },
  locationFieldCol: {
    flex: 0.8,
    minWidth: 220,
  },
  timelineFieldCol: {
    flex: 0.7,
    minWidth: 220,
  },
  publishedConfigSubCard: {
    flex: 1.2,
    minWidth: 280,
    minHeight: 78,
    backgroundColor: AdminColors.blue25,
    borderLeftWidth: 4,
    borderLeftColor: AdminColors.blue700,
    borderRadius: 6,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  publishedConfigLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    lineHeight: 18,
    color: TEXT_SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  publishedConfigValue: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.blue900,
    marginTop: 4,
  },
  publishedConfigSubtext: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: TEXT_SECONDARY,
    marginTop: 3,
  },

  // ── Warning box (.people-config-warning) ──────────────────────────────────
  warningBox: {
    backgroundColor: '#FEF9DA',
    borderWidth: 1,
    borderColor: '#FF8C09',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 16,
  },
  warningText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: '#814705',
    lineHeight: 20,
  },
  guidanceText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: TEXT_SECONDARY,
    margin: 0,
  },

  // ── Card 2: Glidepath 3-col Grid (.people-config-milestones) ──────────────
  glidepathRow: {
    flexDirection: 'row',
    gap: 24,
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  glidepathCol: {
    flex: 1,
    minWidth: 180,
  },

  // ── Card 3: Table Styles (.people-config-table) ───────────────────────────
  tableScrollWrap: {
    width: '100%',
  },
  tableScrollContent: {
    minWidth: '100%',
    flexGrow: 1,
  },
  table: {
    width: '100%',
    minWidth: '100%',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    width: '100%',
    backgroundColor: AdminColors.blue700,
    minHeight: 56,
    alignItems: 'stretch',
  },
  thCellWeek: {
    width: 170,
    paddingHorizontal: 14,
    paddingVertical: 8,
    justifyContent: 'center',
  },
  thCellMetric: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  thText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    lineHeight: 18,
    color: AdminColors.textOnDark,
    textAlign: 'center',
  },
  thSubText: {
    fontFamily: FontFamily.regular,
    fontSize: 10,
    lineHeight: 14,
    color: AdminColors.textOnDark,
    opacity: 0.9,
    marginTop: 2,
    textAlign: 'center',
  },

  tableRow: {
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
    minHeight: 56,
    backgroundColor: AdminColors.bgSurface,
  },
  tableRowHover: {
    backgroundColor: AdminColors.blue25,
  },
  tdCellWeek: {
    width: 170,
    alignSelf: 'stretch',
    paddingHorizontal: 14,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AdminColors.blue25,
  },
  tdWeekLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: TEXT_PRIMARY,
  },
  milestoneTag: {
    backgroundColor: AdminColors.blue75,
    height: 24,
    borderRadius: 4,
    paddingHorizontal: 8,
    marginLeft: 8,
    justifyContent: 'center',
  },
  milestoneTagText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.blue800,
  },
  tdCellMetric: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricInput: {
    width: '100%',
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: INPUT_BORDER,
    borderRadius: 6,
    height: 32,
    paddingVertical: 4,
    paddingHorizontal: 12,
    textAlign: 'center',
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: TEXT_PRIMARY,
  },

  // ── Card 3: Footer Actions (.people-config-actions) ───────────────────────
  tableFooterActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 24,
    paddingVertical: 16,
    backgroundColor: AdminColors.nearWhite,
    borderTopWidth: 1,
    borderTopColor: '#C6DCFB',
  },

  // ── Toast Notification ────────────────────────────────────────────────────
  toastContainer: {
    position: 'absolute',
    bottom: 24,
    alignSelf: 'center',
    backgroundColor: TEXT_PRIMARY,
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    zIndex: 9999,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  toastText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    color: AdminColors.textOnDark,
  },
});
